import { MenuOutlined } from "@ant-design/icons";
import type { MenuProps } from "antd";
import { Button, Drawer, Layout, Menu, Spin } from "antd";
import { onAuthStateChanged } from "firebase/auth";
import type React from "react";
import { lazy, Suspense, useEffect, useMemo, useRef, useState } from "react";
import { QueryClient, QueryClientProvider, useQuery, useQueryClient } from "react-query";
import { Navigate, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import Logo from "../assets/logo.svg";
import ErrorBoundary from "../components/ErrorBoundary";
import ThemeToggle from "../components/ThemeToggle";
import { PANEL_ROLES, RESTRICTED_EMAILS } from "../constants/access";
import { useTheme } from "../context/ThemeContext";
import { auth } from "../firebase";
import { useBreakpoint } from "../hooks/useBreakpoint";
import { logout } from "../services/auth";
import { fetchProfileInfo } from "../services/profile";
import { ensureSession, SessionLimitError } from "../services/session";
import { buildMenuItems, LOGOUT_KEY, selectedKeyForPath } from "./menuConfig";

const { Content, Sider, Header } = Layout;

const TicketListPage = lazy(() => import("../pages/tickets"));
const LoginPage = lazy(() => import("../pages/auth/login"));
const SessionsPage = lazy(() => import("../pages/auth/sessions"));
const LiveTicketsPage = lazy(() => import("../pages/liveTickets"));
const Movies = lazy(() => import("../pages/movies"));
const Reports = lazy(() => import("../pages/reports"));
const Collections = lazy(() => import("../pages/collection"));
const TicketStatusPage = lazy(() => import("../pages/ticketStatus/index"));
const UserVerificationPage = lazy(() => import("../pages/user"));
const ErrorReportsPage = lazy(() => import("../pages/errorReports"));
const EmailTemplateListPage = lazy(() => import("../pages/emailStudio/TemplateList"));
const EmailTemplateEditorPage = lazy(() => import("../pages/emailStudio/TemplateEditor"));
const EmailCampaignListPage = lazy(() => import("../pages/emailStudio/CampaignList"));
const EmailCampaignEditorPage = lazy(() => import("../pages/emailStudio/CampaignEditor"));
const EmailAnalyticsPage = lazy(() => import("../pages/emailStudio/Analytics"));
const MuseumPassesPage = lazy(() => import("../pages/museumPasses"));

const MainLayout: React.FC<{ collapsed: boolean; onCollapse: (collapsed: boolean) => void }> = ({
  collapsed,
  onCollapse,
}) => {
  const location = useLocation();
  const navigate = useNavigate();
  const { isMobile } = useBreakpoint();
  const { isDark } = useTheme();
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [authUser, setAuthUser] = useState(auth.currentUser);
  const [authResolved, setAuthResolved] = useState(Boolean(auth.currentUser));
  const authUID = authUser?.uid ?? null;
  const rootQueryClient = useQueryClient();

  const sessionEnsuredRef = useRef<string | null>(null);

  const {
    data: profile,
    isLoading: profileLoading,
    isError: profileError,
    refetch: refetchProfile,
  } = useQuery(["profile-info", authUID], fetchProfileInfo, {
    refetchOnWindowFocus: false,
    retry: false,
    enabled: Boolean(authUID) && !location.pathname.startsWith("/auth/"),
    staleTime: 5 * 60 * 1000,
  });

  // --------------- role & email derivation ---------------
  const derivedRole = useMemo(() => {
    if (!profile) return undefined;
    const possibleRoles = [
      profile.role,
      profile.user?.role,
      profile.profile?.role,
      profile.edges?.user?.role,
      profile.data?.role,
    ].filter((value): value is string => typeof value === "string");

    if (possibleRoles.length === 0 && typeof profile.is_admin === "boolean") {
      return profile.is_admin ? "admin" : undefined;
    }

    return possibleRoles[0]?.toLowerCase();
  }, [profile]);

  const userEmail = useMemo(() => {
    const firebaseEmail = authUser?.email?.toLowerCase() ?? "";
    if (!profile) return firebaseEmail;
    const possibleEmails = [
      profile.email,
      profile.user?.email,
      profile.profile?.email,
      profile.edges?.user?.email,
      profile.data?.email,
    ].filter((value): value is string => typeof value === "string");
    return possibleEmails[0]?.toLowerCase() ?? firebaseEmail;
  }, [profile, authUser]);

  // All workspace queries and mutations belong to one authenticated account.
  // Retiring the client also isolates late responses from a previous login.
  const accountKey = `${authUID}:${derivedRole}:${userEmail}`;
  const accountQueryClient = useMemo(
    () => new QueryClient({ defaultOptions: rootQueryClient.getDefaultOptions() }),
    [accountKey, rootQueryClient],
  );
  useEffect(() => () => accountQueryClient.clear(), [accountQueryClient]);

  // --------------- access-level flags ---------------
  const isAdmin = derivedRole === "admin" || derivedRole === "superadmin";
  const hasFullAccess = PANEL_ROLES.includes(derivedRole ?? "");
  const isRestrictedEmailUser = !hasFullAccess && RESTRICTED_EMAILS.includes(userEmail);
  const hasAnyAccess = hasFullAccess || isRestrictedEmailUser;

  const menuItems = useMemo(
    () => buildMenuItems({ isAdmin, isRestrictedEmailUser }),
    [isAdmin, isRestrictedEmailUser],
  );
  const selectedKeys = selectedKeyForPath(location.pathname);

  useEffect(() => {
    const unsubscribe = onAuthStateChanged(auth, async (user) => {
      setAuthUser(user);
      setAuthResolved(true);
      if (!user) {
        sessionEnsuredRef.current = null;
        return;
      }
      if (location.pathname.startsWith("/auth/")) return;
      if (sessionEnsuredRef.current === user.uid) return;
      try {
        await ensureSession();
        sessionEnsuredRef.current = user.uid;
      } catch (error) {
        if (error instanceof SessionLimitError) {
          navigate("/auth/sessions", { replace: true });
        } else {
          console.error("Failed to ensure session", error);
        }
      }
    });

    return () => unsubscribe();
  }, [location.pathname, navigate]);

  const handleLogout = async () => {
    await logout();
    navigate("/auth/login", { replace: true });
  };

  const handleMenuClick: MenuProps["onClick"] = async ({ key }) => {
    if (key === LOGOUT_KEY) {
      await handleLogout();
      return;
    }
    setDrawerOpen(false);
    navigate(key);
  };

  const loadingSpinner = (
    <div style={{ display: "flex", justifyContent: "center", paddingTop: 64 }}>
      <Spin size="large" />
    </div>
  );

  const profileErrorView = (
    <div style={{ textAlign: "center", paddingTop: 64 }}>
      <p style={{ color: "var(--color-text-secondary)", marginBottom: 16 }}>
        Could not load your profile. Please check your connection and try again.
      </p>
      <Button onClick={() => refetchProfile()}>Retry</Button>
    </div>
  );

  /** Wrap a page element with role / email access checks. */
  const guardRoute = (element: React.ReactElement, requireFullAccess = true) => {
    if (!authResolved) return loadingSpinner;
    if (!authUID) return <Navigate to="/auth/login" replace />;
    if (profileLoading) return loadingSpinner;
    if (profileError) return profileErrorView;
    if (!hasAnyAccess) return <Navigate to="/auth/login" replace />;
    if (requireFullAccess && !hasFullAccess) {
      return <Navigate to={isRestrictedEmailUser ? "/movies" : "/auth/login"} replace />;
    }
    return element;
  };

  /** Wrap admin-only pages: spinner while loading, error view on failure. */
  const gateAdmin = (element: React.ReactElement) => {
    if (!authResolved) return loadingSpinner;
    if (!authUID) return <Navigate to="/auth/login" replace />;
    if (profileLoading) return loadingSpinner;
    if (profileError) return profileErrorView;
    return isAdmin ? element : <Navigate to="/reports" replace />;
  };

  const menu = (
    <Menu
      theme={isDark ? "dark" : "light"}
      selectedKeys={selectedKeys}
      mode="inline"
      items={menuItems}
      onClick={handleMenuClick}
      style={{ background: "var(--color-bg-sidebar)" }}
      className="custom-menu"
    />
  );

  return (
    <Layout style={{ marginTop: "10px", background: "var(--color-bg-primary)" }}>
      {!location.pathname.startsWith("/auth/") && !isMobile && (
        <Sider
          collapsible
          collapsed={collapsed}
          onCollapse={onCollapse}
          style={{ background: "var(--color-bg-sidebar)", padding: "9px" }}
          className="custom-sider"
        >
          <div style={{ padding: "16px", textAlign: "center" }}>
            <img
              src={Logo}
              alt="Logo"
              style={{
                maxWidth: "100%",
                height: "auto",
                marginBottom: "16px",
              }}
            />
          </div>
          <div style={{ display: "flex", justifyContent: "center", marginBottom: 8 }}>
            <ThemeToggle />
          </div>
          {profileLoading ? (
            <div style={{ display: "flex", justifyContent: "center", paddingTop: 32 }}>
              <Spin />
            </div>
          ) : (
            menu
          )}
        </Sider>
      )}
      {!location.pathname.startsWith("/auth/") && isMobile && (
        <>
          <Header
            style={{
              background: "var(--color-bg-header)",
              padding: "0 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              position: "sticky",
              top: 0,
              zIndex: 10,
              height: 56,
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: 8, minWidth: 0 }}>
              <img src={Logo} alt="Logo" style={{ height: 28 }} />
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
              <ThemeToggle />
              <Button
                type="text"
                icon={<MenuOutlined style={{ fontSize: 22 }} />}
                aria-label="Open navigation menu"
                onClick={() => setDrawerOpen(true)}
              />
            </div>
          </Header>
          <Drawer
            placement="left"
            width={280}
            open={drawerOpen}
            onClose={() => setDrawerOpen(false)}
            styles={{ body: { padding: 12, background: "var(--color-bg-sidebar)" } }}
          >
            <div style={{ textAlign: "center", marginBottom: 12 }}>
              <img src={Logo} alt="Logo" style={{ maxWidth: "70%", height: "auto" }} />
            </div>
            {menu}
          </Drawer>
        </>
      )}
      <Layout style={{ background: "var(--color-bg-primary)" }}>
        <Content
          style={{
            overflow: "auto",
            height: "100%",
            padding: "9px",
            background: "var(--color-bg-primary)",
          }}
        >
          <div
            style={{
              padding: "10px 20px",
              minHeight: 360,
              background: "var(--color-bg-primary)",
            }}
          >
            <QueryClientProvider key={accountKey} client={accountQueryClient}>
              <ErrorBoundary>
                <Suspense
                  fallback={
                    <div style={{ display: "flex", justifyContent: "center", paddingTop: 64 }}>
                      <Spin size="large" />
                    </div>
                  }
                >
                  <Routes>
                    <Route path="/auth/login" element={<LoginPage />} />
                    <Route path="/auth/sessions" element={<SessionsPage />} />
                    {/* Full-access-only routes */}
                    <Route path="/tickets" element={guardRoute(<TicketListPage />)} />
                    <Route path="/reports" element={guardRoute(<Reports />)} />
                    <Route path="/liveTickets" element={guardRoute(<LiveTicketsPage />)} />
                    <Route path="/collections" element={guardRoute(<Collections />)} />
                    <Route path="/ticket-status" element={guardRoute(<TicketStatusPage />)} />
                    <Route
                      path="/museum-passes"
                      element={guardRoute(
                        <MuseumPassesPage
                          key={`${authUID}:${derivedRole}`}
                          isAdmin={isAdmin}
                          viewerKey={authUID ?? ""}
                        />,
                      )}
                    />
                    <Route
                      path="/error-reports"
                      element={guardRoute(<ErrorReportsPage isAdmin={isAdmin} />)}
                    />
                    {/* Email Studio (admin only) */}
                    <Route
                      path="/email-studio/templates"
                      element={gateAdmin(<EmailTemplateListPage />)}
                    />
                    <Route
                      path="/email-studio/templates/new"
                      element={gateAdmin(<EmailTemplateEditorPage />)}
                    />
                    <Route
                      path="/email-studio/templates/edit/:uuid"
                      element={gateAdmin(<EmailTemplateEditorPage />)}
                    />
                    <Route
                      path="/email-studio/campaigns"
                      element={gateAdmin(<EmailCampaignListPage />)}
                    />
                    <Route
                      path="/email-studio/campaigns/new"
                      element={gateAdmin(<EmailCampaignEditorPage />)}
                    />
                    <Route
                      path="/email-studio/campaigns/edit/:uuid"
                      element={gateAdmin(<EmailCampaignEditorPage />)}
                    />
                    <Route
                      path="/email-studio/analytics"
                      element={gateAdmin(<EmailAnalyticsPage />)}
                    />
                    {/* Movie Ticket: accessible to both full-access and restricted users */}
                    <Route path="/movies" element={guardRoute(<Movies />, false)} />
                    <Route path="/users" element={gateAdmin(<UserVerificationPage />)} />
                    <Route path="/" element={<Navigate to="/auth/login" replace />} />
                    <Route path="*" element={<Navigate to="/auth/login" replace />} />
                  </Routes>
                </Suspense>
              </ErrorBoundary>
            </QueryClientProvider>
          </div>
        </Content>
      </Layout>
    </Layout>
  );
};

export default MainLayout;
